import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AuthLayout,
  FormError,
  GoogleIcon,
  OrDivider,
  PasswordInput,
  startGoogleSignIn,
} from '../components/AuthLayout';
import { isGoogleAuthAvailable } from '../utils/googleAuth';
import { getErrorMessage } from '../utils/errors';
import { getPostAuthPath, rememberAfterVerifyPath } from '../utils/redirect';
import CheckEmailNotice from '../components/CheckEmailNotice';
import { useDocumentTitle } from '../hooks/use-document-title';

const MIN_PASSWORD_LENGTH = 8;

export default function RegisterPage() {
  useDocumentTitle('Create your account');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [sendFailed, setSendFailed] = useState(false);

  const googleAvailable = isGoogleAuthAvailable();
  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  useEffect(() => {
    if (user) navigate(getPostAuthPath(location.state, user), { replace: true });
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordTouched(true);
      setError(`Please choose a password with at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setLoading(true);
    try {
      const result = await register(email.trim(), password, name.trim());
      if (result.verification_required) {
        rememberAfterVerifyPath(location.state);
        setSendFailed(result.email_sent === false);
        setSentTo(result.email || email.trim());
        return;
      }
      toast.success('Your account is ready. Mazel tov!');
      navigate(getPostAuthPath(location.state, result.user), { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'We could not create your account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    if (!startGoogleSignIn()) {
      toast.error('Google sign-up is temporarily unavailable. Please use your email instead.');
    }
  };

  if (sentTo) {
    return (
      <AuthLayout
        title="Check your email"
        footer={<Link to="/login" className="text-gold hover:underline font-medium">Back to sign in</Link>}
      >
        <CheckEmailNotice email={sentTo} sendFailed={sendFailed} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start creating beautiful invitations today"
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" state={location.state} className="text-gold hover:underline font-medium">
            Sign in
          </Link>
        </>
      }
    >
      {googleAvailable && (
        <>
          <Button type="button" variant="outline" onClick={handleGoogle} className="w-full h-10 [&_svg]:size-5">
            <GoogleIcon />
            Sign up with Google
          </Button>
          <OrDivider label="or sign up with email" />
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input
            id="name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            className="mt-2"
            data-testid="register-name"
          />
        </div>

        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            className="mt-2"
            data-testid="register-email"
          />
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setPasswordTouched(true)}
            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={72}
            className="mt-2"
            aria-describedby="password-hint"
            aria-invalid={(passwordTouched && passwordTooShort) || undefined}
            data-testid="register-password"
          />
          <p
            id="password-hint"
            className={`mt-1.5 text-xs ${passwordTouched && passwordTooShort ? 'text-destructive' : 'text-ink/50'}`}
          >
            Use at least {MIN_PASSWORD_LENGTH} characters.
          </p>
        </div>

        <FormError message={error} />

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-10 bg-gold text-white hover:bg-gold/90"
          data-testid="register-submit"
        >
          {loading ? 'Creating account…' : 'Create Account'}
        </Button>

        <p className="text-xs text-ink/50 text-center">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="underline hover:text-ink">Terms</Link> and{' '}
          <Link to="/privacy-policy" className="underline hover:text-ink">Privacy Policy</Link>.
        </p>
      </form>
    </AuthLayout>
  );
}
